import { coreModelAtom, modelAtom } from "@/financeModels/owasco/model";
import { isModelReadOnlyFromHookAtom } from "@/hooks/useFinancialDeckPermissions";
import { getShortId } from "@/utils/id";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { atom } from "jotai";
import { atomEffect } from "jotai-effect";

import { getCurrentFinancialDeckEntity } from "@/models/financialDecks";
import { PersistableModel } from "./types";

const saveRevisionInterval = 40;
let revisionSaveCounter = 0;

export const lastSaveAttemptIdAtom = atom("");
export const lastSaveCompletedIdAtom = atom("");
export const lastSaveErrorIdAtom = atom("");

export const isSavingAtom = atom((get) => {
  return get(lastSaveAttemptIdAtom) !== get(lastSaveCompletedIdAtom);
});

export const isSaveErrorAtom = atom((get) => {
  return get(lastSaveAttemptIdAtom) === get(lastSaveErrorIdAtom);
});

let hasSetLoadedModel = false;
const loadedModelFromFirebaseAtom = atom<PersistableModel | null>(null);
loadedModelFromFirebaseAtom.onMount = (setAtom) => {
  hasSetLoadedModel = false;

  const deckEntity = getCurrentFinancialDeckEntity();
  const docRef = doc(deckEntity.docRef, "revision", "latest");

  getDoc(docRef)
    .then((doc) => {
      const data = doc.data();

      //console.log("Loaded model from firebase", data);
      setAtom(data as PersistableModel);
    })
    .catch((error) => {
      console.warn("Error loading model from firebase", error);
    });
};

export const saveToFirebaseEffect = atomEffect((get, set) => {
  const model = get(coreModelAtom);
  const deckEntity = getCurrentFinancialDeckEntity();
  const revisionRef = collection(deckEntity.docRef, "revision");
  const modelDocRef = doc(revisionRef, "latest");

  // Load the model from firebase on mount
  if (get(loadedModelFromFirebaseAtom) === null) return;
  if (!hasSetLoadedModel) {
    set(modelAtom, get(loadedModelFromFirebaseAtom) || model);
    hasSetLoadedModel = true;
    return;
  }

  const saveActionId = getShortId();
  set(lastSaveAttemptIdAtom, saveActionId);

  if (get(isModelReadOnlyFromHookAtom)) return;

  //console.log("Saving model to firebase...", model);
  setDoc(modelDocRef, { ...model, createdAt: serverTimestamp() })
    .then(() => {
      set(lastSaveCompletedIdAtom, saveActionId);
      revisionSaveCounter++;

      if (revisionSaveCounter % saveRevisionInterval === 0) {
        addDoc(revisionRef, {
          ...model,
          createdAt: serverTimestamp(),
        });
      }
    })
    .catch((e) => {
      console.log("Error saving model to firebase", e);
      set(lastSaveErrorIdAtom, saveActionId);
    });

  deckEntity.update({
    excerpt: {
      targetPurchasePrice: model?.general?.targetPurchasePrice || null,
      cashFlow: model?.general?.cashFlow || null,
    },
  });
});

export const hasFinishedLoadingModelOnMountAtom = atom((get) => {
  return get(loadedModelFromFirebaseAtom) !== null;
});
